import React from "react";
import { Eye, PhoneCall, Share2 } from "lucide-react";
import {
  getPrivacyOverlayLabel,
  shouldBlurProfileImage,
} from "../../../utils/imagePrivacy";
import { ProfilePreviewCardProps } from "../dashboard.types";

const ProfilePreviewCard: React.FC<ProfilePreviewCardProps> = ({
  profile,
  scoreLabel,
  callLabel,
  viewLabel,
  shareLabel,
  showActions = true,
  layout = "scroll",
  badgeLabel,
  badgeClassName,
  onView,
  onShare,
  onCall,
  extraActions,
}) => {
  const blurPhoto = shouldBlurProfileImage(profile.imagePrivacy);

  const completionValue =
    typeof profile.matchScore === "string" ||
    typeof profile.matchScore === "number"
      ? profile.matchScore
      : profile.completion;

  const scoreValue =
    completionValue !== undefined &&
    completionValue !== null &&
    completionValue !== ""
      ? String(completionValue)
      : "--";

  const privacyLabel = blurPhoto
    ? getPrivacyOverlayLabel(profile.imagePrivacy)
    : null;

  const handleKeyboardOpen = (
    event: React.KeyboardEvent<HTMLDivElement>,
  ) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onView(profile);
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onView(profile)}
      onKeyDown={handleKeyboardOpen}
      className={`min-w-0 overflow-hidden rounded-[18px] p-2.5 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg ${
        layout === "grid" ? "w-full" : "w-[145px] flex-none sm:w-[170px]"
      }`}
    >
      <div className="relative overflow-hidden rounded-2xl bg-slate-50">
        <img
          src={profile.image}
          alt={profile.name}
          className={`aspect-[4/4.4] w-full object-cover transition-transform duration-300 ${
            blurPhoto ? "scale-110 blur-sm brightness-90" : ""
          }`}
        />

        {badgeLabel ? (
          <div
            className={`absolute bottom-2 left-2 rounded-full px-2.5 py-1 text-[10px] font-semibold text-white shadow-sm ${
              badgeClassName || "bg-violet-600"
            }`}
          >
            {badgeLabel}
          </div>
        ) : null}

        {privacyLabel ? (
          <div className="absolute inset-x-2 bottom-2 rounded-full bg-black/60 px-2 py-1 text-center text-[10px] font-semibold text-white backdrop-blur-sm">
            {privacyLabel}
          </div>
        ) : null}
      </div>

      <div className="mt-2 space-y-1">
        <p className="truncate text-sm font-semibold text-slate-900">
          {profile.name}
        </p>

        <p className="truncate text-[11px] font-medium text-slate-900">
          {profile.maritalId || "N/A"} | {profile.age || "--"} yrs |{" "}
          {profile.height || "--"}
        </p>

        <p className="text-[11px] font-medium text-slate-900">
          {scoreLabel}: {scoreValue}
        </p>
      </div>

      {showActions ? (
        <div className="mt-3 grid grid-cols-3 gap-2">
          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onCall?.(profile);
            }}
            disabled={!onCall}
            className={`inline-flex items-center justify-center gap-1 rounded-xl px-2 py-2 text-[11px] font-semibold transition ${
              onCall
                ? "bg-slate-100 text-slate-700 hover:bg-slate-200"
                : "cursor-not-allowed bg-slate-100 text-slate-400"
            }`}
          >
            <PhoneCall size={13} />
            {callLabel}
          </button>

          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onView(profile);
            }}
            className="inline-flex items-center justify-center gap-1 rounded-xl bg-violet-50 px-2 py-2 text-[11px] font-semibold text-violet-700 transition hover:bg-violet-100"
          >
            <Eye size={13} />
            {viewLabel}
          </button>

          <button
            type="button"
            onClick={(event) => {
              event.stopPropagation();
              onShare(profile);
            }}
            className="inline-flex items-center justify-center gap-1 rounded-xl bg-pink-50 px-2 py-2 text-[11px] font-semibold text-pink-700 transition hover:bg-pink-100"
          >
            <Share2 size={13} />
            {shareLabel}
          </button>
        </div>
      ) : null}

      {extraActions ? (
        <div
          className="mt-2"
          onClick={(event) => event.stopPropagation()}
        >
          {extraActions}
        </div>
      ) : null}
    </div>
  );
};

export default ProfilePreviewCard;